import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, Step } from "react-joyride";

import FindId from "./FindId";
import { useTheme } from "@/comn/hooks";

const FindAccount = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();

    /* ---------------- Guided tour state ---------------- */
    const [runTour, setRunTour] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const tourStartedRef = useRef(false);

    useEffect(() => {
        // Remove previous tour flag
        localStorage.removeItem("findAccountTour");

        setIsMounted(true);

        if (tourStartedRef.current) return;

        const hasSeenTour = localStorage.getItem("findAccountTour");

        if (!hasSeenTour) {
            const startTour = () => {
                if (!tourStartedRef.current) {
                    tourStartedRef.current = true;
                    setRunTour(true);
                }
            };
            setTimeout(startTour, 300);
        }
    }, []);

    /* ---------------- Joyride steps ---------------- */
    const steps: Step[] = [
        {
            target: ".find-header",
            content: <>This page helps you recover your account credentials if you have forgotten your User ID.</>,
            disableBeacon: true,
            placement: "bottom",
        },
        {
            target: ".tab-id-btn",
            content: (
                <>
                    Enter the information below to retrieve your <strong>User ID</strong>. The required fields are
                    explained in the next step.
                </>
            ),
            disableBeacon: true,
            placement: "bottom",
        },
        {
            target: ".find-body",
            content: (
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md shadow-sm">
                    <p className="text-sm">
                        Enter your <strong>FULL NAME</strong> and the <strong>VALID EMAIL</strong> used during account
                        registration (e.g. JUMA ALI, juma@gmail.com).
                    </p>
                </div>
            ),
            disableBeacon: true,
            placement: "top",
        },
    ];

    const validSteps = isMounted
        ? steps.filter((step) => {
              if (step.target === ".find-header" || step.target === ".tab-id-btn") {
                  return document.querySelector(step.target);
              }
              return true;
          })
        : [];

    /* ---------------- Joyride callback ---------------- */
    const handleJoyrideCallback = (data: CallBackProps) => {
        const { index, status } = data;

        setCurrentStep(index);

        if (status === "finished" || status === "skipped") {
            setRunTour(false);
            localStorage.setItem("findAccountTour", "true");
        }
    };

    /* ---------------- Theme-driven classes ---------------- */
    const headerClass = classNames(
        "find-header p-[30px] relative rounded-t-[30px]",
        theme.isDark ? "bg-card text-primary" : "bg-primary text-white",
    );

    const tabBase = "px-[14px] py-[6px] text-xs rounded-2xl transition ml-1";

    const tabActive = "bg-primary text-white";

    const tabInactive = "bg-transparent text-primary border border-primary hover:bg-primary hover:text-white";

    return (
        <div className="w-full h-auto lg:h-[calc(100vh-250px)] flex items-center relative">
            {/* ---------------- Joyride (UNCHANGED behavior) ---------------- */}
            {isMounted && validSteps.length > 0 && (
                <Joyride
                    steps={validSteps}
                    run={runTour}
                    continuous={true}
                    showSkipButton={true}
                    showProgress={true}
                    scrollToFirstStep={true}
                    disableOverlayClose={false}
                    spotlightClicks={false}
                    hideBackButton={false}
                    styles={{
                        options: {
                            zIndex: 10000,
                            primaryColor: "#007bff",
                            width: 450, // Wider tooltip
                            arrowColor: "#fff",
                        },
                        tooltipContainer: {
                            padding: "20px",
                            borderRadius: "12px",
                        },
                    }}
                    locale={{
                        back: "Back",
                        close: "Close",
                        last: "Finish",
                        next: "Next",
                        skip: "Skip",
                    }}
                    callback={handleJoyrideCallback}
                />
            )}

            <div className="w-full flex items-center justify-center my-[70px]">
                <div className="w-full md:w-[520px] lg:w-[520px]">
                    {/* ---------------- Header ---------------- */}
                    <div className="find-header bg-primary p-[30px] relative rounded-t-[30px]">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl text-white tab-id-btn">{t("L_FIND_ID")}</h2>
                        </div>

                        {/* Decorative image placeholder (kept for consistency) */}
                        <span className="block w-[180px] lg:w-[236px] absolute -top-[84px] -right-[10px] lg:-right-[80px]">
                            {/* intentionally empty */}
                        </span>
                    </div>

                    {/* ---------------- Body ---------------- */}
                    <div className="find-body bg-card rounded-b-[30px] shadow-lg">
                        <FindId embedded />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FindAccount;
